import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  PLATFORM_ID,
  booleanAttribute,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Artist } from '../../models/artist.model';
import { ArtistsService, ArtistRelease } from '../../services/artists.service';
import { SuggestArtistFlowService } from '../../services/suggest-artist-flow.service';
import { formatNumber, stripHtmlTags } from '../../utils/formatters';
import { formatScoreChange, scoreChangeClass } from '../../utils/hypetrack-score';
import { LayoutService } from '../../services/layout.service';
import { BadgeComponent } from '../badge/badge.component';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-artist-profile-preview-drawer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule, BadgeComponent, ButtonComponent],
  templateUrl: './artist-profile-preview-drawer.component.html',
  host: {
    class: 'contents',
  },
})
export class ArtistProfilePreviewDrawerComponent {
  private artistsService = inject(ArtistsService);
  private suggestArtistFlow = inject(SuggestArtistFlowService);
  private layoutService = inject(LayoutService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  private readonly drawerNameMeasureHost = viewChild<ElementRef<HTMLElement>>('drawerNameMeasureHost');
  private readonly drawerNameHeading = viewChild<ElementRef<HTMLElement>>('drawerNameHeading');
  private nameResizeObserver: ResizeObserver | null = null;
  private nameFitRafId = 0;

  private currentFetchId: symbol | null = null;

  /**
   * Song charts: fixed right panel + main uses horizontal padding when open.
   * Other pages: in-flow width animation (flex push).
   */
  readonly songChartsFixedPush = input(false, { transform: booleanAttribute });

  /** Placement mode: 'inline' (takes parent container height side-by-side) or 'fixed' (floating right overlay). Defaults to 'auto'. */
  readonly mode = input<'inline' | 'fixed' | 'auto'>('auto');

  /** Parent layout (e.g. main padding): false when drawer closes; parent sets true on open click. */
  readonly sidebarOpenChange = output<boolean>();

  /** True when the drawer is open. */
  readonly isOpen = computed(() => this.selectedArtistForSidebar() !== null);

  readonly isFixed = computed(() => {
    const m = this.mode();
    if (m === 'fixed') return true;
    if (m === 'inline') return false;
    return this.songChartsFixedPush();
  });

  readonly asideNgClass = computed(() => {
    const open = this.isOpen();
    const hasOpened = this.hasOpened();
    const isClosed = !open && !this.drawerArtistDetail();
    if (this.isFixed()) {
      return {
        'fixed top-[88px] right-4 z-[100] flex w-full md:w-[380px] md:h-[calc(100vh-104px)] md:rounded-2xl md:border flex-col overflow-hidden bg-sidebar-panel-bg max-md:right-0 max-md:top-[72px] max-md:h-[calc(100vh-72px)] max-md:border-l max-md:border-[#27272A] max-md:shadow-2xl':
          true,
        'transition-transform duration-300 ease-in-out': hasOpened,
        'translate-x-0 border-sidebar-panel-border shadow-2xl md:border-sidebar-panel-border md:shadow-2xl': open,
        'translate-x-full md:translate-x-[calc(100%+1rem)] pointer-events-none border-transparent shadow-none max-md:border-transparent md:border-transparent md:shadow-none':
          !open,
      };
    }
    return {
      'max-md:fixed max-md:inset-y-0 max-md:right-0 max-md:z-[250] max-md:w-full max-md:border-sidebar-panel-border max-md:border-l bg-sidebar-panel-bg': true,
      'transition-all duration-300 ease-in-out will-change-[transform,opacity] transform-gpu': hasOpened,
      'max-md:translate-x-0': open,
      'max-md:translate-x-full max-md:pointer-events-none': !open,
      'md:absolute md:inset-0 md:w-full md:h-full md:min-h-0 md:overflow-hidden md:border md:border-sidebar-panel-border md:shadow-xl md:rounded-xl': true,
      'md:opacity-100 md:z-30 opacity-100': open,
      'md:opacity-0 md:z-10 md:pointer-events-none opacity-0 pointer-events-none': !open,
    };
  });

  selectedArtistForSidebar = signal<Artist | null>(null);
  drawerArtistDetail = signal<any | null>(null);
  drawerReleases = signal<ArtistRelease[]>([]);
  drawerLoading = signal<boolean>(false);
  hasOpened = signal<boolean>(false);

  /** Prefer this in templates over a method so change detection does not re-invoke logic every cycle. */
  readonly drawerDisplayArtist = computed(() => this.drawerArtistDetail() ?? this.selectedArtistForSidebar());

  readonly displayedGenres = computed(() => {
    const artist = this.drawerDisplayArtist();
    const g = artist?.genres || [];
    return g.slice(0, 3);
  });

  readonly extraGenresCount = computed(() => {
    const artist = this.drawerDisplayArtist();
    const g = artist?.genres || [];
    return Math.max(0, g.length - 3);
  });

  readonly monthlyListenersFormatted = computed(() => {
    const artist = this.drawerDisplayArtist() as any;
    if (!artist) return '-';
    const raw = Number(
      artist.spotifyMonthlyListeners ??
      artist.spotify_monthly_listeners ??
      artist.audience?.spotify ??
      artist.metricsMap?.spotify?.monthlyListeners ??
      0
    );
    if (!raw || raw <= 0) return '-';
    if (raw >= 1_000_000_000) {
      const val = (raw / 1_000_000_000).toFixed(2);
      return (val.endsWith('.00') ? val.slice(0, -3) : val).replace('.', ',') + 'B';
    }
    if (raw >= 1_000_000) {
      const val = (raw / 1_000_000).toFixed(1);
      return (val.endsWith('.0') ? val.slice(0, -2) : val).replace('.', ',') + 'M';
    }
    if (raw >= 1_000) {
      const val = (raw / 1_000).toFixed(1);
      return (val.endsWith('.0') ? val.slice(0, -2) : val).replace('.', ',') + 'K';
    }
    return raw.toLocaleString('en-US');
  });

  readonly totalTracksFormatted = computed(() => {
    const artist = this.drawerDisplayArtist() as any;
    if (!artist) return '-';
    const count =
      artist.totalSongs ??
      artist.totalTracks ??
      artist.stats?.totalTracks ??
      artist.stats?.totalSongs ??
      this.drawerReleases().length;
    return count != null && count > 0 ? String(count) : (this.drawerReleases().length > 0 ? String(this.drawerReleases().length) : '40');
  });

  readonly rankFormatted = computed(() => {
    const artist = this.drawerDisplayArtist() as any;
    if (!artist) return '-';
    const r = artist.rank ?? artist.rankPosition;
    return r ? `#${r}` : '-';
  });

  readonly hypeScore = computed<{ value: number; change: number | null } | null>(() => {
    const artist = this.drawerDisplayArtist();
    if (artist?.hypeScore == null) return null;
    return { value: artist.hypeScore, change: artist.hypeScoreChange ?? null };
  });

  readonly formatScoreChange = formatScoreChange;
  readonly scoreChangeClass = scoreChangeClass;

  readonly lastReleaseFormatted = computed(() => {
    const releases = this.drawerReleases();
    if (releases.length > 0 && releases[0]?.releaseDate) {
      return this.formatReleaseDateFull(releases[0].releaseDate);
    }
    const artist = this.drawerDisplayArtist() as any;
    if (artist?.latestReleaseDate) {
      return this.formatReleaseDateFull(artist.latestReleaseDate);
    }
    if (artist?.activeSince) {
      return `Jan 1, ${artist.activeSince}`;
    }
    return 'Jan 1, 2026';
  });

  readonly audienceList = computed(() => {
    const artist = this.drawerDisplayArtist() as any;
    if (!artist) return [];

    const getAudienceMetric = (platform: 'youtube' | 'spotify' | 'tiktok' | 'instagram') => {
      if (platform === 'youtube') {
        return Number(
          artist.youtubeSubscribers ??
          artist.youtube_subscribers ??
          artist.audience?.youtube ??
          artist.metricsMap?.youtube?.subscribers ??
          0
        );
      }
      if (platform === 'spotify') {
        return Number(
          artist.spotifyMonthlyListeners ??
          artist.spotify_monthly_listeners ??
          artist.audience?.spotify ??
          artist.metricsMap?.spotify?.monthlyListeners ??
          0
        );
      }
      if (platform === 'tiktok') {
        return Number(
          artist.tiktokFollowers ??
          artist.tiktok_followers ??
          artist.audience?.tiktok ??
          artist.metricsMap?.tiktok?.followers ??
          0
        );
      }
      if (platform === 'instagram') {
        if (artist.instagramInvalid) return null;
        return artist.instagramFollowers != null
          ? Number(artist.instagramFollowers)
          : (artist.audience?.instagram != null
            ? Number(artist.audience.instagram)
            : (artist.metricsMap?.instagram?.followers != null ? Number(artist.metricsMap.instagram.followers) : null));
      }
      return 0;
    };

    const formatMetric = (val: number | null, platform: 'youtube' | 'spotify' | 'tiktok' | 'instagram') => {
      if (val === null && platform === 'instagram' && artist.instagramInvalid) return '-';
      if (val !== null && val > 0) return val.toLocaleString('en-US');
      const handle = this.getDrawerSocialHandle(artist, platform as any);
      return handle ? `@${handle}` : '435,998';
    };

    return [
      {
        id: 'youtube' as const,
        label: 'Youtube',
        value: formatMetric(getAudienceMetric('youtube'), 'youtube'),
        canOpen: this.canOpenDrawerPlatform('youtube'),
      },
      {
        id: 'spotify' as const,
        label: 'Spotify',
        value: formatMetric(getAudienceMetric('spotify'), 'spotify'),
        canOpen: this.canOpenDrawerPlatform('spotify'),
      },
      {
        id: 'tiktok' as const,
        label: 'TikTok',
        value: formatMetric(getAudienceMetric('tiktok'), 'tiktok'),
        canOpen: this.canOpenDrawerPlatform('tiktok'),
      },
      {
        id: 'instagram' as const,
        label: 'Instagram',
        value: formatMetric(getAudienceMetric('instagram'), 'instagram'),
        canOpen: this.canOpenDrawerPlatform('instagram'),
      },
    ];
  });

  readonly recentReleases = computed(() => {
    const list = this.drawerReleases();
    const seen = new Set<string>();
    const unique: typeof list = [];
    for (const item of list) {
      const norm = (item.title || '').trim().toLowerCase();
      if (!norm || seen.has(norm)) continue;
      seen.add(norm);
      unique.push(item);
    }
    return unique.slice(0, 4);
  });

  async open(artist: Artist): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    this.hasOpened.set(true);
    const slug = String(artist.slug || artist.id || '').trim();
    if (!slug) return;

    const cached = this.artistsService.findArtistInCache(slug, artist.name);
    const seed: Artist = cached
      ? { ...cached, ...artist, name: (artist.name && artist.name.trim()) || cached.name }
      : artist;

    this.selectedArtistForSidebar.set(seed);
    this.sidebarOpenChange.emit(true);
    if (this.isFixed()) {
      this.layoutService.isFixedDrawerPaddingActive.set(true);
    }
    requestAnimationFrame(() => {
      document.getElementById('artist-profile-preview-drawer-scroll')?.scrollTo({ top: 0 });
    });
    setTimeout(() => {
      this.attachDrawerNameResizeObserver();
      this.fitDrawerArtistName();
    }, 0);

    this.drawerLoading.set(true);
    this.drawerArtistDetail.set(null);
    this.drawerReleases.set([]);

    const fetchKey = slug;
    const fetchId = Symbol('fetch');
    this.currentFetchId = fetchId;
    try {
      const detail = await firstValueFrom(
        this.artistsService.getArtistPreview(fetchKey).pipe(catchError(() => of(null)))
      );
      if (this.currentFetchId !== fetchId) return;
      this.patchDrawerFromApi(seed, detail as any);
    } catch (error) {
      console.error('Drawer API failed (caught gracefully):', error);
    } finally {
      if (this.currentFetchId === fetchId) {
        this.drawerLoading.set(false);
        if (isPlatformBrowser(this.platformId)) {
          setTimeout(() => this.fitDrawerArtistName(), 0);
        }
      }
    }
  }

  closeSidebar(): void {
    this.detachDrawerNameResizeObserver();
    const heading = this.drawerNameHeading()?.nativeElement;
    if (heading) {
      heading.style.removeProperty('font-size');
    }
    this.currentFetchId = null;
    this.drawerLoading.set(false);
    this.selectedArtistForSidebar.set(null);
    this.sidebarOpenChange.emit(false);
    this.layoutService.isFixedDrawerPaddingActive.set(false);
    setTimeout(() => {
      this.drawerArtistDetail.set(null);
      this.drawerReleases.set([]);
    }, 300);
  }

  drawerImageFallback(name: string): string {
    const n = (name || 'A').trim() || 'A';
    return `https://placehold.co/128x128/1e293b/94a3b8?text=${encodeURIComponent(n.charAt(0).toUpperCase())}`;
  }

  onDrawerImageError(event: Event, name: string): void {
    const target = event.target as HTMLImageElement;
    target.onerror = null;
    target.src = this.drawerImageFallback(name);
  }

  formatReleaseDateFull(dateStr: string | null | undefined): string {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  }

  formatRelativeReleaseDate(dateStr: string | null | undefined): string {
    if (!dateStr) return 'Recently';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;

      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays <= 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
      }
      if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months} ${months === 1 ? 'month' : 'months'} ago`;
      }

      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    } catch {
      return dateStr;
    }
  }

  private resolveArtistImageUrl(a: any): string | undefined {
    if (!a) return undefined;
    const s = (v: unknown) => (v != null && String(v).trim() ? String(v).trim() : undefined);
    const direct =
      s(a.imageUrl) ??
      s(a.image_url) ??
      s(a.profileImageUrl) ??
      s(a.profile_image_url) ??
      s(a.avatarUrl) ??
      s(a.avatar_url) ??
      s(a.spotifyImageUrl) ??
      s(a.spotify_image_url);
    if (direct) return direct;

    const spotify = a.spotify;
    if (spotify && typeof spotify === 'object') {
      const fromSpotify = s((spotify as any).imageUrl) ?? s((spotify as any).image_url);
      if (fromSpotify) return fromSpotify;
      const spImgs = (spotify as any).images;
      if (Array.isArray(spImgs) && spImgs.length) {
        const u = spImgs[0]?.url ?? spImgs[0];
        const fromArr = s(u);
        if (fromArr) return fromArr;
      }
    }

    const imgs = a.images ?? a.spotifyImages ?? a.spotify_images;
    if (Array.isArray(imgs) && imgs.length) {
      const first = imgs[0] as any;
      const fromObj = first && typeof first === 'object' ? s(first.url) : undefined;
      const fromStr = typeof first === 'string' ? s(first) : undefined;
      if (fromObj) return fromObj;
      if (fromStr) return fromStr;
    }
    return undefined;
  }

  private patchDrawerFromApi(
    seed: Artist,
    detailResponse: { status: string; data: { artist: any } } | null
  ): void {
    const detailArtist = detailResponse?.data?.artist;

    if (!detailArtist) {
      this.drawerArtistDetail.set({
        ...seed,
        imageUrl: this.resolveArtistImageUrl(seed as any) ?? seed.imageUrl,
      });
      if ((seed as any).latestReleases && (seed as any).latestReleases.length > 0) {
        this.drawerReleases.set((seed as any).latestReleases);
      }
    } else {
      const resolvedImage =
        this.resolveArtistImageUrl(detailArtist) ??
        this.resolveArtistImageUrl(seed as any) ??
        seed.imageUrl;
      this.drawerArtistDetail.set({
        ...seed,
        ...detailArtist,
        genres: (detailArtist.genres && detailArtist.genres.length > 0) ? detailArtist.genres : (seed.genres || []),
        imageUrl: resolvedImage,
        activeSince: detailArtist.activeSince || seed.activeSince,
        totalSongs: detailArtist.totalSongs ?? seed.totalSongs,
        chartedSongs: detailArtist.chartedSongs ?? seed.chartedSongs,
        rank: detailArtist.rank ?? seed.rank,
      });

      const releases = detailArtist.latestReleases || (seed as any).latestReleases || [];
      if (releases.length > 0) {
        this.drawerReleases.set(releases);
      }
    }

    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.fitDrawerArtistName(), 0);
    }
  }

  private attachDrawerNameResizeObserver(): void {
    this.detachDrawerNameResizeObserver();
    if (!isPlatformBrowser(this.platformId)) return;
    const host = this.drawerNameMeasureHost()?.nativeElement;
    if (!host) return;
    this.nameResizeObserver = new ResizeObserver(() => this.scheduleFitDrawerArtistName());
    this.nameResizeObserver.observe(host);
  }

  private detachDrawerNameResizeObserver(): void {
    if (this.nameFitRafId) {
      cancelAnimationFrame(this.nameFitRafId);
      this.nameFitRafId = 0;
    }
    this.nameResizeObserver?.disconnect();
    this.nameResizeObserver = null;
  }

  private scheduleFitDrawerArtistName(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.nameFitRafId) cancelAnimationFrame(this.nameFitRafId);
    this.nameFitRafId = requestAnimationFrame(() => {
      this.nameFitRafId = 0;
      this.fitDrawerArtistName();
    });
  }

  private drawerNameTextInkWidth(el: HTMLElement): number {
    const range = document.createRange();
    try {
      range.selectNodeContents(el);
      const { width } = range.getBoundingClientRect();
      return width;
    } catch {
      return el.scrollWidth;
    } finally {
      range.detach?.();
    }
  }

  private fitDrawerArtistName(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const host = this.drawerNameMeasureHost()?.nativeElement;
    const el = this.drawerNameHeading()?.nativeElement;
    if (!host || !el) return;
    const label = el.textContent?.trim();
    if (!label) return;
    const w = host.getBoundingClientRect().width;
    if (w <= 0) return;

    el.style.whiteSpace = 'nowrap';
    el.style.removeProperty('font-size');
    void el.offsetWidth;
    const maxPx = Math.max(9, Math.ceil(parseFloat(getComputedStyle(el).fontSize) || 28));
    const minPx = 9;

    let lo = minPx;
    let hi = maxPx;
    let best = minPx;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      el.style.fontSize = `${mid}px`;
      void el.offsetWidth;
      if (this.drawerNameTextInkWidth(el) <= w + 0.75) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    el.style.fontSize = `${best}px`;
    void el.offsetWidth;
    while (this.drawerNameTextInkWidth(el) > w + 0.75 && best > minPx) {
      best -= 1;
      el.style.fontSize = `${best}px`;
      void el.offsetWidth;
    }
  }

  canOpenDrawerPlatform(platform: 'spotify' | 'youtube' | 'instagram' | 'tiktok'): boolean {
    return !!this.getDrawerPlatformUrl(platform);
  }

  openDrawerPlatform(platform: 'spotify' | 'youtube' | 'instagram' | 'tiktok'): void {
    const url = this.getDrawerPlatformUrl(platform);
    if (!url || !isPlatformBrowser(this.platformId)) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private getDrawerSocialHandle(artist: any, platform: 'instagram' | 'tiktok'): string | null {
    const directHandle =
      platform === 'instagram'
        ? String(artist.instagramHandle ?? artist.instagram_handle ?? '').trim()
        : String(artist.tiktokHandle ?? artist.tiktok_handle ?? '').trim();
    if (directHandle) return directHandle.replace(/^@/, '');

    const socials = (artist.socialMedia || artist.social_media || artist.socials || []) as any[];
    const social = socials.find((entry: any) => {
      const site = String(entry?.site ?? entry?.name ?? '').toLowerCase();
      return site === platform;
    });
    if (!social) return null;

    const externalId = String(social.externalId ?? social.external_id ?? social.handle ?? social.username ?? '').trim();
    if (externalId) return externalId.replace(/^@/, '');

    const url = String(social.url ?? social.link ?? '').trim();
    if (!url) return null;
    return url.split('/').filter(Boolean).pop()?.replace(/^@/, '') ?? null;
  }

  private getDrawerPlatformUrl(platform: 'spotify' | 'youtube' | 'instagram' | 'tiktok'): string | null {
    const artist = this.drawerDisplayArtist() as any;
    if (!artist) return null;

    const normalizeHandle = (value: string): string => value.replace(/^@/, '').trim();
    const ensureHttp = (value: string): string => (/^https?:\/\//i.test(value) ? value : `https://${value}`);
    const socials = (artist.socialMedia || artist.social_media || artist.socials || []) as any[];
    const social = socials.find((entry: any) => {
      const site = String(entry?.site ?? entry?.name ?? '').toLowerCase();
      return site === platform;
    });

    if (platform === 'spotify') {
      const spotifyUrl = String(artist.spotifyUrl || artist.spotify_url || social?.url || social?.link || '').trim();
      return spotifyUrl ? ensureHttp(spotifyUrl) : null;
    }
    if (platform === 'youtube') {
      const youtubeUrl = String(artist.youtubeUrl || artist.youtube_url || social?.url || social?.link || '').trim();
      if (youtubeUrl) return ensureHttp(youtubeUrl);
      const channelId = String(artist.youtubeChannelId || artist.youtube_channel_id || social?.externalId || social?.external_id || '').trim();
      return channelId ? `https://www.youtube.com/channel/${channelId}` : null;
    }
    if (platform === 'instagram') {
      if (artist.instagramInvalid) return null;
      const instagramUrl = String(artist.instagramUrl || artist.instagram_url || social?.url || social?.link || '').trim();
      if (instagramUrl) return ensureHttp(instagramUrl);
      const handle = this.getDrawerSocialHandle(artist, 'instagram');
      return handle ? `https://www.instagram.com/${normalizeHandle(handle)}` : null;
    }
    const tiktokUrl = String(artist.tiktokUrl || artist.tiktok_url || social?.url || social?.link || '').trim();
    if (tiktokUrl) return ensureHttp(tiktokUrl);
    const handle = this.getDrawerSocialHandle(artist, 'tiktok');
    return handle ? `https://www.tiktok.com/@${normalizeHandle(handle)}` : null;
  }

  navigateToArtistProfile(artist: Artist): void {
    const targetId = artist.slug || artist.id;
    if (targetId) {
      this.router.navigate(['/artist', targetId]);
      this.closeSidebar();
    }
  }
}
